<template>
  <div class="p-5">
    <!-- Back Navigation -->
    <div class="mb-4">
      <button
        @click="$router.push('/phm/population-assessment')"
        class="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
      >
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="text-xs" />
        Back to Dashboard
      </button>
    </div>

    <!-- Loading State with Elapsed Timer -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-16 gap-3">
      <LoadingIndicator message="Generating assessment report..." />
      <p class="text-xs text-gray-400">{{ elapsedSeconds }}s elapsed</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-700">{{ error }}</p>
      <button @click="fetchReport" class="mt-2 text-sm text-red-600 underline">Retry</button>
    </div>

    <!-- Report Content -->
    <template v-else-if="report">
      <!-- Report Header -->
      <div class="flex items-start justify-between mb-6">
        <div>
          <h1 class="text-xl font-bold text-gray-900">Population Health Assessment Report</h1>
          <p class="text-sm text-gray-500 mt-1">
            Report Date: {{ report.report_date }} · Total Population: {{ formatNumber(report.total_population) }}
          </p>
        </div>
        <ExportMenu :disabled="!report" :exports="[{ type: 'pdf', action: downloadPdf, loading: exportingPdf }]" />
      </div>

      <!-- Executive Summary -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
        <h2 class="text-sm font-semibold text-blue-900 mb-2">Executive Summary</h2>
        <p class="text-sm text-blue-800 leading-relaxed">{{ report.executive_summary }}</p>
      </div>

      <!-- Cross-Factor Priorities -->
      <div
        v-if="report.cross_factor_priorities && report.cross_factor_priorities.length"
        class="border border-gray-200 rounded-lg overflow-hidden mb-6"
      >
        <div class="bg-gray-50 px-5 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">Cross-Factor Priorities</h3>
        </div>
        <div class="p-5 space-y-3">
          <div
            v-for="item in report.cross_factor_priorities"
            :key="item.priority"
            class="flex items-start gap-3"
          >
            <span class="shrink-0 w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center mt-0.5">
              {{ item.priority }}
            </span>
            <div>
              <div class="text-sm font-medium text-gray-900">{{ item.population }}</div>
              <p class="text-xs text-gray-600 mt-0.5">{{ item.rationale }}</p>
              <div class="flex gap-1.5 mt-1">
                <span
                  v-for="factor in item.factors_involved"
                  :key="factor"
                  class="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                >{{ factor }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Cards -->
      <div class="space-y-5">
        <div
          v-for="section in report.sections"
          :key="section.id"
          class="border border-gray-200 rounded-lg overflow-hidden"
        >
          <!-- Section Header -->
          <div class="bg-gray-50 px-5 py-3 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-semibold text-gray-900">{{ section.title }}</h3>
              <span v-if="section.ncqa_factor" class="text-xs text-gray-400">
                NCQA Factor {{ section.ncqa_factor }}
              </span>
            </div>
          </div>

          <div class="p-5 space-y-4">
            <!-- Population Summary -->
            <p class="text-sm text-gray-700 leading-relaxed">{{ section.population_summary }}</p>

            <!-- Two-column layout: Metrics + Characteristics -->
            <div class="grid grid-cols-2 gap-5">
              <!-- Metrics Table -->
              <div v-if="section.metrics_table && section.metrics_table.length">
                <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Metrics</h4>
                <table class="w-full text-xs">
                  <thead>
                    <tr class="border-b border-gray-200">
                      <th class="text-left py-1.5 text-gray-500 font-medium">Metric</th>
                      <th class="text-right py-1.5 text-gray-500 font-medium">Value</th>
                      <th class="text-right py-1.5 text-gray-500 font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in section.metrics_table" :key="idx" class="border-b border-gray-100">
                      <td class="py-1.5 text-gray-700">{{ row.metric }}</td>
                      <td class="py-1.5 text-right text-gray-900 font-medium">{{ row.value }}</td>
                      <td class="py-1.5 text-right text-gray-500">{{ row.percentage }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Key Characteristics -->
              <div v-if="section.key_characteristics && section.key_characteristics.length">
                <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Key Characteristics</h4>
                <div class="space-y-2">
                  <div
                    v-for="(char, idx) in section.key_characteristics"
                    :key="idx"
                    class="bg-gray-50 rounded p-2.5"
                  >
                    <div class="text-xs font-medium text-gray-900">{{ char.label }}</div>
                    <div class="text-xs text-gray-600 mt-0.5">{{ char.detail }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Identified Needs -->
            <div v-if="section.identified_needs && section.identified_needs.length">
              <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Identified Needs</h4>
              <ul class="space-y-1.5">
                <li
                  v-for="(need, idx) in section.identified_needs"
                  :key="idx"
                  class="flex items-start gap-2 text-xs text-gray-700"
                >
                  <span class="text-amber-500 mt-0.5 shrink-0">●</span>
                  {{ need }}
                </li>
              </ul>
            </div>

            <!-- Recommended Actions -->
            <div v-if="section.recommended_actions && section.recommended_actions.length">
              <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recommended Actions</h4>
              <ul class="space-y-1.5">
                <li
                  v-for="(action, idx) in section.recommended_actions"
                  :key="idx"
                  class="flex items-start gap-2 text-xs text-gray-700"
                >
                  <span class="text-green-500 mt-0.5 shrink-0">●</span>
                  {{ action }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation Warnings -->
      <div
        v-if="report.validation_warnings && report.validation_warnings.length"
        class="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer"
        @click="showWarnings = !showWarnings"
      >
        <div class="flex items-center gap-2 text-xs text-amber-700">
          <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
          <span>Report generated with {{ report.validation_warnings.length }} quality warning{{ report.validation_warnings.length > 1 ? 's' : '' }}. Click to {{ showWarnings ? 'hide' : 'review' }}.</span>
        </div>
        <ul v-if="showWarnings" class="mt-2 space-y-1 pl-5">
          <li
            v-for="(warning, idx) in report.validation_warnings"
            :key="idx"
            class="text-xs text-amber-600 list-disc"
          >{{ warning }}</li>
        </ul>
      </div>

      <!-- Methodology & Data Sources (collapsible) -->
      <div v-if="report.methodology" class="mt-6 border border-gray-200 rounded-lg overflow-hidden">
        <div
          class="bg-gray-50 px-5 py-3 border-b border-gray-200 cursor-pointer flex items-center justify-between"
          @click="showMethodology = !showMethodology"
        >
          <h3 class="text-sm font-semibold text-gray-900">Methodology & Data Sources</h3>
          <font-awesome-icon
            :icon="['fas', showMethodology ? 'chevron-up' : 'chevron-down']"
            class="text-xs text-gray-400"
          />
        </div>

        <div v-if="showMethodology" class="p-5 space-y-4 text-xs text-gray-700">
          <!-- Assessment Period -->
          <div>
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Assessment Period</h4>
            <p>{{ report.methodology.assessment_period }}</p>
            <p v-if="report.methodology.baseline_note" class="mt-1 text-gray-500 italic">
              {{ report.methodology.baseline_note }}
            </p>
          </div>

          <!-- Data Sources -->
          <div>
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Data Sources</h4>
            <ul class="space-y-0.5 pl-4">
              <li v-for="(src, idx) in report.methodology.data_sources" :key="idx" class="list-disc">{{ src }}</li>
            </ul>
          </div>

          <!-- Data Currency -->
          <div v-if="report.methodology.data_currency">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Data Currency & Lag</h4>
            <div class="space-y-1.5">
              <p v-if="report.methodology.data_currency.claims_lag">{{ report.methodology.data_currency.claims_lag }}</p>
              <p v-if="report.methodology.data_currency.ehr_lag">{{ report.methodology.data_currency.ehr_lag }}</p>
              <p v-if="report.methodology.data_currency.enrollment_lag">{{ report.methodology.data_currency.enrollment_lag }}</p>
            </div>
          </div>

          <!-- Attribution Logic -->
          <div v-if="report.methodology.attribution_logic">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Attribution Logic</h4>
            <p>{{ report.methodology.attribution_logic }}</p>
          </div>

          <!-- Continuous Enrollment -->
          <div v-if="report.methodology.continuous_enrollment">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Continuous Enrollment</h4>
            <p>{{ report.methodology.continuous_enrollment }}</p>
          </div>

          <!-- Measurement Definitions -->
          <div v-if="report.methodology.measurement_definitions">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Measurement Definitions</h4>
            <div class="space-y-1.5">
              <div v-for="(defn, key) in report.methodology.measurement_definitions" :key="key">
                <span class="font-medium text-gray-900">{{ formatMethodLabel(key) }}:</span>
                <span class="ml-1">{{ defn }}</span>
              </div>
            </div>
          </div>

          <!-- Overlap Counting -->
          <div v-if="report.methodology.overlap_counting">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Overlap Counting</h4>
            <p>{{ report.methodology.overlap_counting }}</p>
          </div>

          <!-- HCC Model -->
          <div v-if="report.methodology.hcc_model">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Risk Adjustment Model</h4>
            <p>{{ report.methodology.hcc_model }}</p>
          </div>

          <!-- Suppression Policy -->
          <div v-if="report.methodology.suppression_policy">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Small-Cell Suppression</h4>
            <p>{{ report.methodology.suppression_policy }}</p>
          </div>

          <!-- Excluded Populations -->
          <div v-if="report.methodology.excluded_populations">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Excluded Populations</h4>
            <p>{{ report.methodology.excluded_populations }}</p>
          </div>

          <!-- Factor Definitions -->
          <div>
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-2">Factor Definitions</h4>
            <div class="space-y-2">
              <div v-for="(factor, key) in report.methodology.factor_definitions" :key="key" class="bg-gray-50 rounded p-2.5">
                <span class="font-medium text-gray-900">{{ factor.name }}:</span>
                <span class="ml-1">{{ factor.method }}</span>
              </div>
            </div>
          </div>

          <!-- Benchmark Sources -->
          <div v-if="report.methodology.benchmark_sources">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-1">Benchmark Sources</h4>
            <ul class="space-y-0.5 pl-4">
              <li v-for="(src, idx) in report.methodology.benchmark_sources" :key="idx" class="list-disc">{{ src }}</li>
            </ul>
          </div>

          <!-- Evaluation Methodology -->
          <div v-if="report.methodology.evaluation_methodology">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-2">Evaluation Methodology</h4>
            <div class="space-y-2">
              <div v-if="report.methodology.evaluation_methodology.measurement_cadence" class="bg-gray-50 rounded p-2.5">
                <span class="font-medium text-gray-900">Measurement Cadence:</span>
                <span class="ml-1">{{ report.methodology.evaluation_methodology.measurement_cadence }}</span>
              </div>
              <div v-if="report.methodology.evaluation_methodology.intervention_impact" class="bg-gray-50 rounded p-2.5">
                <span class="font-medium text-gray-900">Intervention Impact Measurement:</span>
                <span class="ml-1">{{ report.methodology.evaluation_methodology.intervention_impact }}</span>
              </div>
              <div v-if="report.methodology.evaluation_methodology.trending_plan" class="bg-gray-50 rounded p-2.5">
                <span class="font-medium text-gray-900">Year 2 Trending Plan:</span>
                <span class="ml-1">{{ report.methodology.evaluation_methodology.trending_plan }}</span>
              </div>
            </div>
          </div>

          <!-- Member Experience Data -->
          <div v-if="report.methodology.member_experience">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-2">Member Experience Data</h4>
            <div class="space-y-2">
              <div class="bg-amber-50 rounded p-2.5 border border-amber-100">
                <span class="font-medium text-amber-800">Status:</span>
                <span class="ml-1 text-amber-700">{{ report.methodology.member_experience.status }}</span>
              </div>
              <p>{{ report.methodology.member_experience.detail }}</p>
              <div class="bg-gray-50 rounded p-2.5">
                <span class="font-medium text-gray-900">Planned Enhancements:</span>
                <span class="ml-1">{{ report.methodology.member_experience.planned_enhancements }}</span>
              </div>
            </div>
          </div>

          <!-- Benchmark Comparisons -->
          <div v-if="report.benchmark_comparisons && report.benchmark_comparisons.length">
            <h4 class="font-semibold text-gray-500 uppercase tracking-wider mb-2">Benchmark Comparisons</h4>
            <table class="w-full">
              <thead>
                <tr class="border-b border-gray-200">
                  <th class="text-left py-1.5 text-gray-500 font-medium">Metric</th>
                  <th class="text-right py-1.5 text-gray-500 font-medium">This Population</th>
                  <th class="text-right py-1.5 text-gray-500 font-medium">National</th>
                  <th class="text-right py-1.5 text-gray-500 font-medium">Ratio</th>
                  <th class="text-left py-1.5 text-gray-500 font-medium pl-3">Source</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(bm, idx) in report.benchmark_comparisons" :key="idx">
                  <tr v-if="bm.tenant_value" class="border-b border-gray-100">
                    <td class="py-1.5 text-gray-700">
                      {{ bm.metric }}
                      <span v-if="bm.stale" class="ml-1 text-amber-500" title="Benchmark data is >3 years old">*</span>
                    </td>
                    <td class="py-1.5 text-right text-gray-900 font-medium">{{ bm.tenant_value }}</td>
                    <td class="py-1.5 text-right text-gray-500">{{ bm.benchmark_value }}</td>
                    <td class="py-1.5 text-right" :class="bm.direction === 'above' ? 'text-red-600' : bm.direction === 'below' ? 'text-green-600' : 'text-gray-500'">
                      {{ bm.ratio }}x
                    </td>
                    <td class="py-1.5 text-gray-400 pl-3">{{ bm.source }}</td>
                  </tr>
                  <tr v-if="bm.note" :key="idx + '-note'" class="border-b border-gray-100">
                    <td colspan="5" class="py-1 pl-4 text-gray-400 italic">{{ bm.note }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import jsPDF from 'jspdf';
import { beginSensitiveEgress } from '@/utils/demoEgress';

export default {
  name: 'PHM_AssessmentReport',
  components: { ExportMenu, LoadingIndicator },
  data() {
    return {
      loading: false,
      exportingPdf: false,
      error: null,
      report: null,
      showMethodology: false,
      showWarnings: false,
      elapsedSeconds: 0,
      elapsedTimer: null,
      pollTimer: null,
    };
  },
  mounted() {
    this.fetchReport();
  },
  beforeUnmount() {
    this.stopTimer();
    this.stopPolling();
  },
  methods: {
    startTimer() {
      this.elapsedSeconds = 0;
      this.elapsedTimer = setInterval(() => { this.elapsedSeconds++; }, 1000);
    },
    stopTimer() {
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async fetchReport() {
      this.loading = true;
      this.error = null;
      this.startTimer();
      try {
        const { data } = await this.$axios.post('/api/phm/assessment-report/');
        this.pollJobStatus(data.job_id);
      } catch (err) {
        this.loading = false;
        this.stopTimer();
        this.error = err.response?.data?.detail || 'Failed to start report generation';
      }
    },
    pollJobStatus(jobId) {
      this.pollTimer = setInterval(async () => {
        try {
          const { data } = await this.$axios.get(`/api/jobs/${jobId}/status/`);
          if (data.status === 'completed') {
            this.stopPolling();
            this.report = data.result_data;
            this.loading = false;
            this.stopTimer();
          } else if (data.status === 'failed') {
            this.stopPolling();
            this.error = data.error_message || 'Report generation failed. Please try again.';
            this.loading = false;
            this.stopTimer();
          }
        } catch (err) {
          this.stopPolling();
          this.error = 'Failed to check report status';
          this.loading = false;
          this.stopTimer();
        }
      }, 3000);
    },

    formatNumber(num) {
      if (num == null) return '0';
      return Number(num).toLocaleString();
    },

    formatMethodLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },

    cleanTextForPdf(text) {
      if (!text) return '';
      return text
        .normalize('NFKD')
        .replace(/[^\x20-\x7E\n\r\t]/g, (char) => {
          const map = {
            '\u2192': '->', '\u2190': '<-', '\u2194': '<->',
            '\u2018': "'", '\u2019': "'", '\u201c': '"', '\u201d': '"',
            '\u2013': '-', '\u2014': '--', '\u2026': '...',
            '\u2011': '-', '\u00A0': ' '
          };
          return map[char] || '';
        });
    },

    async downloadPdf() {
      if (!this.report) return;
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;
      this.exportingPdf = true;
      try {
      const doc = new jsPDF('p', 'pt');
      const ct = (t) => this.cleanTextForPdf(t);
      const fmtNum = (n) => this.formatNumber(n);

      // ── FONTS: Load Oswald + IBM Plex Sans ──
      const loadFont = async (filename, family, style) => {
        const resp = await fetch(`/fonts/${filename}`);
        const buf = await resp.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const b64 = btoa(binary);
        doc.addFileToVFS(filename, b64);
        doc.addFont(filename, family, style);
      };
      await Promise.all([
        loadFont('IBMPlexSans-Regular.ttf', 'IBMPlexSans', 'normal'),
        loadFont('IBMPlexSans-Bold.ttf', 'IBMPlexSans', 'bold'),
        loadFont('IBMPlexSans-Regular.ttf', 'IBMPlexSans', 'italic'),
        loadFont('Oswald-Regular.ttf', 'Oswald', 'normal'),
        loadFont('Oswald-Bold.ttf', 'Oswald', 'bold'),
      ]);
      const H = 'Oswald', B = 'IBMPlexSans';

      // ── LAYOUT ──
      const mg = 50, pw = 595, ph = 842, cw = pw - mg * 2;
      let y = mg, pn = 0;

      // ── COLORS ──
      const C = {
        navy: [30,41,59], teal: [15,118,110], tealLight: [204,251,241], tealBg: [240,253,250],
        dark: [30,41,59], body: [51,65,85], muted: [100,116,139],
        light: [248,250,252], border: [226,232,240],
        amber: [180,83,9], amberBg: [255,247,237], amberBorder: [251,191,36],
        red: [185,28,28], redBg: [254,242,242], redBorder: [239,68,68],
        white: [255,255,255], stripe: [241,245,249],
        purple: [88,28,135], purpleBg: [250,245,255],
        blue: [29,78,216], blueBg: [239,246,255],
      };
      const sc = (rgb) => doc.setTextColor(...rgb);
      const sf = (rgb) => doc.setFillColor(...rgb);
      const sd = (rgb) => doc.setDrawColor(...rgb);

      // ── PAGE MANAGEMENT ──
      const pageHeader = () => {
        if (pn > 1) {
          sc(C.muted); doc.setFontSize(7); doc.setFont(B, 'normal');
          doc.text('Population Health Assessment Report', mg, 28);
          doc.text(this.report.report_date, pw - mg, 28, { align: 'right' });
          sd(C.border); doc.setLineWidth(0.5); doc.line(mg, 33, pw - mg, 33);
        }
      };
      const pageFooter = () => {
        sc(C.muted); doc.setFontSize(7); doc.setFont(B, 'normal');
        doc.text(`Page ${pn}`, pw / 2, ph - 25, { align: 'center' });
      };
      const newPage = () => {
        if (pn > 0) { pageFooter(); doc.addPage(); }
        pn++; pageHeader(); y = pn > 1 ? 48 : mg;
      };
      const brk = (n = 60) => {
        if (y > ph - 50 - n) { pageFooter(); doc.addPage(); pn++; pageHeader(); y = 48; }
      };

      // ── TEXT HELPERS ──
      const addPara = (text, x, sz = 9, font = 'normal', maxW = cw, color = C.body) => {
        doc.setFontSize(sz); doc.setFont(B, font); sc(color);
        const lines = doc.splitTextToSize(ct(text || ''), maxW);
        for (const l of lines) { brk(sz + 4); doc.text(l, x, y); y += sz + 3; }
      };

      const addBullets = (items, x, color = C.body, bc = C.teal) => {
        for (const item of items) {
          brk(18); doc.setFontSize(8.5); doc.setFont(B, 'normal');
          sf(bc); doc.circle(x + 3, y - 2.5, 1.5, 'F'); sc(color);
          const bl = doc.splitTextToSize(ct(item), cw - (x - mg) - 12);
          for (const l of bl) { brk(12); doc.text(l, x + 10, y); y += 11; }
          y += 4;
        }
      };

      // ── COMPONENT: Section Header ──
      const sectionHeader = (title, subtitle = '', label = '') => {
        brk(subtitle ? 48 : 34);
        sf(C.navy); doc.rect(mg, y, cw, subtitle ? 32 : 22, 'F');
        doc.setFontSize(13); doc.setFont(H, 'bold'); sc(C.white);
        doc.text(ct(title), mg + 12, y + (subtitle ? 14 : 15));
        if (label) {
          doc.setFontSize(8); doc.setFont(B, 'normal');
          doc.text(label, pw - mg - 12, y + (subtitle ? 14 : 15), { align: 'right' });
        }
        if (subtitle) {
          doc.setFontSize(7.5); doc.setFont(B, 'normal'); sc([180, 200, 220]);
          doc.text(ct(subtitle).substring(0, 130), mg + 12, y + 26);
        }
        y += (subtitle ? 32 : 22) + 18; sc(C.dark);
      };

      // ── COMPONENT: Sub-header ──
      const subHeader = (title) => {
        y += 4;
        brk(26); doc.setFontSize(8.5); doc.setFont(H, 'bold'); sc(C.teal);
        const t = title.toUpperCase(); doc.text(t, mg, y); y += 3;
        sd(C.teal); doc.setLineWidth(0.75); doc.line(mg, y, mg + doc.getTextWidth(t) + 4, y);
        y += 14; sc(C.dark);
      };

      // ── COMPONENT: KPI Cards ──
      const kpiCards = (items, cols = 4) => {
        const gap = 8, cardW = (cw - gap * (cols - 1)) / cols, cardH = 50;
        const rows = Math.ceil(items.length / cols);
        brk(rows * (cardH + 6));
        items.forEach((item, i) => {
          const row = Math.floor(i / cols), col = i % cols;
          const x = mg + col * (cardW + gap), cy = y + row * (cardH + 6);
          sf(C.light); sd(C.border); doc.setLineWidth(0.4); doc.rect(x, cy, cardW, cardH, 'FD');
          sf(C.teal); doc.rect(x, cy, cardW, 2.5, 'F');
          doc.setFontSize(16); doc.setFont(H, 'bold'); sc(C.navy);
          doc.text(String(item.value), x + cardW / 2, cy + 21, { align: 'center' });
          doc.setFontSize(6.5); doc.setFont(B, 'bold'); sc(C.muted);
          doc.text(ct(item.label).toUpperCase().substring(0, 30), x + cardW / 2, cy + 32, { align: 'center' });
          if (item.note) {
            doc.setFontSize(6); doc.setFont(B, 'normal'); sc(C.body);
            doc.text(ct(item.note).substring(0, 45), x + cardW / 2, cy + 42, { align: 'center' });
          }
        });
        y += rows * (cardH + 6) + 8;
      };

      // ── COMPONENT: Metrics Band ──
      const metricsBand = (metrics) => {
        const cols = 3, gap = 8, cardW = (cw - gap * (cols - 1)) / cols, cardH = 36;
        const rows = Math.ceil(metrics.length / cols);
        brk(rows * (cardH + 4) + 4);
        metrics.forEach((m, i) => {
          const row = Math.floor(i / cols), col = i % cols;
          const x = mg + col * (cardW + gap), cy = y + row * (cardH + 4);
          sf(C.light); sd(C.border); doc.setLineWidth(0.3); doc.rect(x, cy, cardW, cardH, 'FD');
          doc.setFontSize(13); doc.setFont(H, 'bold'); sc(C.teal);
          doc.text(String(m.value), x + cardW / 2, cy + 16, { align: 'center' });
          doc.setFontSize(6); doc.setFont(B, 'normal'); sc(C.muted);
          doc.text(ct(m.metric).substring(0, 35), x + cardW / 2, cy + 27, { align: 'center' });
          if (m.percentage && m.percentage !== '-') {
            doc.setFontSize(5.5); sc(C.body);
            doc.text(String(m.percentage), x + cardW / 2, cy + 33, { align: 'center' });
          }
        });
        y += rows * (cardH + 4) + 10;
      };

      // ── COMPONENT: Caveat Box ──
      const caveatBox = (title, text) => {
        doc.setFontSize(8); doc.setFont(B, 'normal');
        const lines = doc.splitTextToSize(ct(text), cw - 24);
        const bH = 14 + lines.length * 10 + 8; brk(bH + 6);
        sf(C.amberBg); doc.rect(mg, y, cw, bH, 'F');
        sf(C.amberBorder); doc.rect(mg, y, 3, bH, 'F');
        doc.setFontSize(7.5); doc.setFont(B, 'bold'); sc(C.amber);
        doc.text(ct(title).toUpperCase(), mg + 12, y + 11);
        doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
        let cy = y + 22; for (const l of lines) { doc.text(l, mg + 12, cy); cy += 10; }
        y += bH + 12;
      };

      // ── COMPONENT: Warning Panel ──
      const warningPanel = (title, text) => {
        doc.setFontSize(8); doc.setFont(B, 'normal');
        const lines = doc.splitTextToSize(ct(text), cw - 24);
        const bH = 14 + lines.length * 10 + 8; brk(bH + 6);
        sf(C.redBg); doc.rect(mg, y, cw, bH, 'F');
        sf(C.redBorder); doc.rect(mg, y, 3, bH, 'F');
        doc.setFontSize(7.5); doc.setFont(B, 'bold'); sc(C.red);
        doc.text(ct(title).toUpperCase(), mg + 12, y + 11);
        doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
        let cy = y + 22; for (const l of lines) { doc.text(l, mg + 12, cy); cy += 10; }
        y += bH + 12;
      };

      // ── COMPONENT: Operational Focus Panel ──
      const opFocus = (headline, metric = '') => {
        doc.setFontSize(9); doc.setFont(B, 'bold');
        const metricW = metric ? doc.getTextWidth(ct(metric)) + 20 : 0;
        const hLines = doc.splitTextToSize(ct(headline), cw - 28 - metricW);
        const boxH = 14 + hLines.length * 12 + 6;
        brk(boxH + 8);
        sf(C.tealBg); doc.rect(mg, y, cw, boxH, 'F');
        sf(C.teal); doc.rect(mg, y, 3, boxH, 'F');
        doc.setFontSize(7); doc.setFont(B, 'bold'); sc(C.teal);
        doc.text('OPERATIONAL FOCUS', mg + 12, y + 10);
        doc.setFontSize(9); doc.setFont(B, 'bold'); sc(C.dark);
        let hy = y + 22; for (const l of hLines) { doc.text(l, mg + 12, hy); hy += 12; }
        if (metric) {
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.teal);
          doc.text(ct(metric), pw - mg - 12, y + 22, { align: 'right' });
        }
        y += boxH + 12;
      };

      // ── COMPONENT: Overlap Callout ──
      const overlapCallout = (name, count, pct, why) => {
        doc.setFontSize(7.5); doc.setFont(B, 'normal');
        const whyLines = doc.splitTextToSize(ct(why), cw - 20);
        const titleStr = `${ct(name)}: ${count}`;
        doc.setFontSize(10); doc.setFont(H, 'bold');
        const titleW = doc.getTextWidth(titleStr);
        doc.setFontSize(8); doc.setFont(B, 'normal');
        const pctStr = pct ? `(${pct})` : '';
        const pctW = pctStr ? doc.getTextWidth(pctStr) + 6 : 0;
        const oneLine = titleW + pctW < cw - 30;
        const extraLine = oneLine ? 0 : 1;
        const boxH = 26 + extraLine * 12 + whyLines.length * 10 + 6;
        brk(boxH + 10);
        sf(C.purpleBg); sd(C.border); doc.setLineWidth(0.3); doc.rect(mg, y, cw, boxH, 'FD');
        doc.setFontSize(7); doc.setFont(B, 'bold'); sc(C.purple);
        doc.text('CROSS-FACTOR OVERLAP', mg + 10, y + 10);
        doc.setFontSize(10); doc.setFont(H, 'bold'); sc(C.dark);
        doc.text(titleStr, mg + 10, y + 22);
        if (pctStr && oneLine) {
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.muted);
          doc.text(pctStr, mg + 10 + titleW + 6, y + 22);
        }
        let nextY = y + 34;
        if (pctStr && !oneLine) {
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.muted);
          doc.text(pctStr, mg + 10, nextY); nextY += 12;
        }
        doc.setFontSize(7.5); doc.setFont(B, 'normal'); sc(C.body);
        for (const l of whyLines) { doc.text(l, mg + 10, nextY); nextY += 10; }
        y += boxH + 12;
      };

      // ── COMPONENT: Data Quality Alert ──
      const dataQualityAlert = (issue, affected, implication) => {
        doc.setFontSize(7.5); doc.setFont(B, 'normal');
        const impLines = doc.splitTextToSize(ct(implication), cw - 20);
        const boxH = 36 + impLines.length * 10 + 4;
        brk(boxH + 8);
        sf(C.amberBg); sd(C.amberBorder); doc.setLineWidth(0.5); doc.rect(mg, y, cw, boxH, 'FD');
        doc.setFontSize(7); doc.setFont(B, 'bold'); sc(C.amber);
        doc.text('DATA QUALITY ALERT', mg + 10, y + 10);
        doc.setFontSize(9); doc.setFont(B, 'bold'); sc(C.dark);
        doc.text(ct(issue), mg + 10, y + 22);
        doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
        doc.text(`Affected: ${ct(affected)}`, mg + 10, y + 33);
        doc.setFontSize(7.5); sc(C.muted);
        let iy = y + 44; for (const l of impLines) { doc.text(l, mg + 10, iy); iy += 10; }
        y += boxH + 12;
      };

      // ── COMPONENT: Structured Action Cards ──
      const structuredActions = (actions) => {
        for (let i = 0; i < actions.length; i++) {
          const raw = ct(actions[i]);
          let title = '', detail = '';
          const colonIdx = raw.indexOf(':');
          const periodIdx = raw.indexOf('.');
          if (colonIdx > 0 && colonIdx < 80) {
            title = raw.substring(0, colonIdx).trim();
            detail = raw.substring(colonIdx + 1).trim();
          } else if (periodIdx > 0 && periodIdx < 120) {
            title = raw.substring(0, periodIdx + 1).trim();
            detail = raw.substring(periodIdx + 1).trim();
          } else {
            title = raw; detail = '';
          }
          doc.setFontSize(8.5); doc.setFont(B, 'bold');
          const titleLines = doc.splitTextToSize(title, cw - 38);
          doc.setFontSize(8); doc.setFont(B, 'normal');
          const detailLines = detail ? doc.splitTextToSize(detail, cw - 38) : [];
          const cardH = 10 + titleLines.length * 11 + (detailLines.length ? 6 + detailLines.length * 10 : 0) + 10;
          brk(cardH + 6);
          sf(C.light); sd(C.border); doc.setLineWidth(0.3); doc.rect(mg, y, cw, cardH, 'FD');
          sf(C.teal); doc.rect(mg, y, 3, cardH, 'F');
          sf(C.teal); doc.circle(mg + 14, y + 12, 6, 'F');
          doc.setFontSize(8); doc.setFont(B, 'bold'); sc(C.white);
          doc.text(String(i + 1), mg + 14, y + 14.8, { align: 'center' });
          doc.setFontSize(8.5); doc.setFont(B, 'bold'); sc(C.dark);
          let ty = y + 13; for (const l of titleLines) { doc.text(l, mg + 28, ty); ty += 11; }
          if (detailLines.length) {
            ty += 3; doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
            for (const l of detailLines) { doc.text(l, mg + 28, ty); ty += 10; }
          }
          y += cardH + 6;
        }
      };

      // ── COMPONENT: Language Ranking Bars ──
      const langRanking = (items) => {
        brk(items.length * 18 + 8);
        const maxVal = Math.max(...items.map(i => parseFloat(String(i.percentage).replace('%', '')) || 0));
        const barMaxW = cw * 0.45;
        for (const item of items) {
          brk(16);
          const pct = parseFloat(String(item.percentage).replace('%', '')) || 0;
          const barW = maxVal > 0 ? (pct / maxVal) * barMaxW : 0;
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.dark);
          doc.text(ct(item.metric), mg, y);
          const valX = mg + 90;
          doc.setFont(B, 'bold'); sc(C.teal); doc.text(String(item.value), valX, y);
          if (item.percentage !== '*') {
            const barX = valX + 35;
            sf(C.tealLight); doc.rect(barX, y - 7, barMaxW, 10, 'F');
            sf(C.teal); doc.rect(barX, y - 7, barW, 10, 'F');
            doc.setFontSize(7); doc.setFont(B, 'normal'); sc(C.muted);
            doc.text(String(item.percentage), barX + barMaxW + 6, y);
          } else {
            doc.setFontSize(7); doc.setFont(B, 'normal'); sc(C.muted);
            doc.text('suppressed (<11)', valX + 35, y);
          }
          y += 16;
        }
        y += 6;
      };

      // ── COMPONENT: Comparison Block ──
      const comparisonBlock = (metric, yours, benchmark, ratio, note) => {
        brk(40);
        sf(C.blueBg); sd(C.border); doc.setLineWidth(0.3); doc.rect(mg, y, cw, 36, 'FD');
        const midX = mg + cw / 2;
        doc.setFontSize(7); doc.setFont(B, 'bold'); sc(C.blue);
        doc.text('BENCHMARK COMPARISON', mg + 10, y + 10);
        doc.setFontSize(11); doc.setFont(H, 'bold'); sc(C.dark);
        doc.text(String(yours), mg + 10, y + 24);
        doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.muted);
        doc.text(ct(metric) + ' (this population)', mg + 10, y + 33);
        doc.setFontSize(11); doc.setFont(H, 'bold'); sc(C.muted);
        doc.text(String(benchmark), midX + 20, y + 24);
        doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.muted);
        doc.text(`national benchmark (${ratio})`, midX + 20, y + 33);
        if (note) {
          doc.setFontSize(7); sc(C.amber);
          doc.text(ct(note).substring(0, 60), pw - mg - 12, y + 10, { align: 'right' });
        }
        y += 50;
      };

      // ── COMPONENT: Metrics Table ──
      const metricsTable = (rows, label = 'ADDITIONAL METRICS') => {
        if (!rows.length) return;
        brk(14 + rows.length * 13);
        doc.setFontSize(7); doc.setFont(B, 'bold'); sc(C.muted); doc.text(label, mg, y); y += 8;
        const tl = mg, cv = mg + 280, cp = mg + 360, tr = mg + 400;
        rows.forEach((row, idx) => {
          brk(13);
          if (idx % 2 === 0) { sf(C.stripe); doc.rect(tl, y - 2, tr - tl, 12, 'F'); }
          doc.setFontSize(7.5); doc.setFont(B, 'normal'); sc(C.dark);
          doc.text(ct(row.metric), tl + 4, y + 6);
          sc(C.body); doc.text(String(row.value), cv, y + 6, { align: 'right' });
          sc(C.muted); doc.text(String(row.percentage), cp, y + 6, { align: 'right' });
          y += 12;
        });
        y += 6;
      };

      // ── COMPONENT: Key Insights ──
      const keyInsights = (characteristics, filterFn) => {
        const chars = filterFn ? characteristics.filter(filterFn) : characteristics;
        for (const ch of chars) {
          brk(20); sf(C.light);
          const lines = doc.splitTextToSize(ct(ch.detail), cw - 18);
          const bH = 14 + lines.length * 10 + 6;
          doc.rect(mg, y, cw, bH, 'F');
          doc.setFontSize(8.5); doc.setFont(B, 'bold'); sc(C.dark);
          doc.text(ct(ch.label), mg + 8, y + 11);
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
          let cy = y + 22; for (const l of lines) { doc.text(l, mg + 8, cy); cy += 10; }
          y += bH + 5;
        }
      };

      const divider = () => { sd(C.border); doc.setLineWidth(0.5); doc.line(mg, y, pw - mg, y); y += 10; };

      // Helper to find section by ID
      const sec = (id) => this.report.sections.find(s => s.id === id);

      // ═══════════════════════════════════════════════════
      // COVER PAGE
      // ═══════════════════════════════════════════════════
      newPage();
      sf(C.navy); doc.rect(0, 0, pw, 6, 'F');
      sf(C.teal); doc.rect(mg, 200, 3, 160, 'F');

      y = 220;
      doc.setFontSize(38); doc.setFont(H, 'bold'); sc(C.navy);
      doc.text('Population Health', mg + 16, y); y += 44;
      doc.text('Assessment Report', mg + 16, y);
      y += 28; sf(C.teal); doc.rect(mg + 16, y, 80, 3, 'F');

      y += 30;
      doc.setFontSize(12); doc.setFont(B, 'normal'); sc(C.body);
      doc.text('Year 1 Baseline Assessment', mg + 16, y);
      y += 18; doc.setFontSize(10); sc(C.muted);
      doc.text('Comprehensive analysis of population health risk, equity, and care management priorities', mg + 16, y);

      y += 35;
      doc.setFontSize(10); doc.setFont(B, 'normal'); sc(C.dark);
      doc.text('Report Date', mg + 16, y); doc.text(this.report.report_date, mg + 120, y); y += 15;
      doc.text('Population', mg + 16, y); doc.setFont(B, 'bold');
      doc.text(fmtNum(this.report.total_population) + ' attributed members', mg + 120, y); y += 15;
      doc.setFont(B, 'normal'); doc.text('Period', mg + 16, y); doc.text('Calendar Year 2025', mg + 120, y);

      sf(C.navy); doc.rect(0, ph - 6, pw, 6, 'F');

      // ═══════════════════════════════════════════════════
      // HOW TO READ THIS REPORT
      // ═══════════════════════════════════════════════════
      newPage();
      sectionHeader('How to Read This Baseline Assessment');

      doc.setFontSize(8.5); doc.setFont(B, 'normal'); sc(C.body);
      const disclaimerItems = [
        'Utilization metrics (ED visits, IP admits, readmission flag rate) are based on the care-managed measurement scope, not the full attributed population. These figures reflect the subset of members actively managed by practices and should not be compared directly to national population-level benchmarks.',
        'A large share of the population (65.8%) is currently unstratified -- meaning insufficient claims or clinical data exists for automated risk assignment. This limits the precision of risk-based analyses.',
        'No continuous enrollment requirement is applied. Members attributed for any portion of the measurement period are included, which maximizes coverage but may include members with incomplete claims histories.',
        'Race/ethnicity coding is incomplete: 17.8% of members are coded as Unknown/Not Specified. Disparity ratios should be interpreted as a preliminary screen, not a definitive equity assessment.',
        'Claims data are subject to approximately 90-day adjudication lag. The effective completeness window extends to roughly 90 days prior to the report date.',
        'Behavioral health identification uses a narrow HCC-based logic (HCC 155, 157/158, 159) that likely understates behavioral complexity relative to broader diagnosis-based or EHR-informed approaches.',
      ];
      for (const item of disclaimerItems) {
        brk(20); doc.setFontSize(8.5); doc.setFont(B, 'normal');
        sf(C.teal); doc.circle(mg + 5, y - 2.5, 1.5, 'F'); sc(C.body);
        const lines = doc.splitTextToSize(ct(item), cw - 18);
        for (const l of lines) { brk(11); doc.text(l, mg + 14, y); y += 11; }
        y += 5;
      }

      // ═══════════════════════════════════════════════════
      // EXECUTIVE DASHBOARD
      // ═══════════════════════════════════════════════════
      newPage();
      sectionHeader('Executive Dashboard', 'Key findings and immediate priorities');

      brk(50);
      sf(C.navy); doc.rect(mg, y, cw, 46, 'F');
      sf(C.teal); doc.rect(mg, y, 3, 46, 'F');
      doc.setFontSize(7); doc.setFont(B, 'bold'); sc(C.tealLight);
      doc.text('BASELINE FINDING', mg + 12, y + 10);
      doc.setFontSize(10); doc.setFont(B, 'bold'); sc(C.white);
      doc.text('Population complexity is concentrated, with significant data maturity gaps', mg + 12, y + 22);
      doc.setFontSize(8); doc.setFont(B, 'normal'); sc([180, 200, 220]);
      doc.text('65.8% unstratified | 81.4% disability-eligible | 17.8% unknown race | CM readmit flag 35.6%', mg + 12, y + 34);
      y += 52;

      kpiCards([
        { label: 'Total Population', value: fmtNum(this.report.total_population), note: 'Attributed members' },
        { label: 'CM Readmit Flag Rate', value: '35.6%', note: 'CM scope, not per-discharge' },
        { label: 'ED Visits / 1,000', value: '2,198', note: 'CM denominator' },
        { label: 'IP Admits / 1,000', value: '1,651', note: 'CM denominator' },
      ], 4);

      kpiCards([
        { label: 'Unstratified', value: '65.8%', note: '2,462 need risk assignment' },
        { label: 'Disability Rate', value: '81.4%', note: 'vs 10.6% national (7.7x)' },
        { label: 'Unknown Race', value: '17.8%', note: '665 limit disparity analysis' },
        { label: 'High-Risk Dual', value: '43.9%', note: '129 of 294 -- prioritization signal' },
      ], 4);

      caveatBox('Denominator Warning',
        'All utilization rates use care-managed member counts as denominators, not the full attributed population. ' +
        'IP admits 1,651/1K vs national 208/1K (7.9x) is explained by this denominator difference. ' +
        'Do not compare directly to CMS/AHRQ national benchmarks.');

      caveatBox('Member Experience Gap',
        'CAHPS and satisfaction survey data not yet incorporated. Planned for Year 2.');

      subHeader('Immediate Operational Priorities');
      addBullets([
        'Complete risk stratification for 2,462 unstratified members (65.8%) to enable targeted care management assignment',
        'Prioritize outreach to 129 high-risk dual-eligible members (43.9% of dual segment) for care plan intensification',
        'Improve race/ethnicity coding completeness -- 17.8% Unknown limits disparity monitoring reliability',
        'Establish language-concordant outreach for LEP members: Spanish (80) and Chinese (23) cover 74% of the LEP population',
      ], mg + 4);
      y += 6;

      // ═══════════════════════════════════════════════════
      // FACTOR 1: General Population & SDOH
      // ═══════════════════════════════════════════════════
      const s1 = sec('general');
      if (s1) {
        newPage();
        sectionHeader('General Population & SDOH', '3,742 members | Risk stratification, SDOH indicators, and utilization baseline', 'Factor 1');

        opFocus('Two-thirds of members lack risk stratification. Case-finding is the top priority.', '65.8% unstratified');

        metricsBand(s1.metrics_table.slice(0, 5));
        metricsTable(s1.metrics_table.slice(5));

        subHeader('Key Insights');
        keyInsights(s1.key_characteristics, c => !c.label.toLowerCase().includes('caveat'));

        const utilCaveat = s1.key_characteristics.find(c => c.label.toLowerCase().includes('caveat'));
        if (utilCaveat) caveatBox('Measurement Caveat', utilCaveat.detail);

        subHeader('Priority Gaps');
        addBullets(s1.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s1.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 2: Children & Adolescents
      // ═══════════════════════════════════════════════════
      const s2 = sec('children');
      if (s2) {
        newPage();
        sectionHeader('Children & Adolescents', '2,352 members (62.9%) | Preventive care priorities by age band', 'Factor 2');

        opFocus('Largest segment. Age-band structure guides preventive care workflow priorities.', '62.9% of population');

        brk(70);
        doc.setFontSize(8); doc.setFont(B, 'bold'); sc(C.muted); doc.text('AGE BAND DISTRIBUTION', mg, y); y += 12;
        const ageBands = [
          { label: 'Ages 0-5', count: '570', pct: 24.2, focus: 'Well-child visits, developmental screening' },
          { label: 'Ages 6-12', count: '1,017', pct: 43.2, focus: 'Preventive care continuity, school-age screening' },
          { label: 'Ages 13-17', count: '765', pct: 32.5, focus: 'Mental health screening, adolescent preventive care' },
        ];
        const barMaxW = cw * 0.35;
        for (const band of ageBands) {
          brk(28);
          const barW = (band.pct / 50) * barMaxW;
          doc.setFontSize(9); doc.setFont(B, 'bold'); sc(C.dark); doc.text(band.label, mg, y);
          doc.setFont(B, 'normal'); sc(C.teal); doc.text(`${band.count} (${band.pct}%)`, mg + 70, y);
          y += 10;
          sf(C.tealLight); doc.rect(mg, y - 5, barMaxW, 8, 'F');
          sf(C.teal); doc.rect(mg, y - 5, barW, 8, 'F');
          doc.setFontSize(7.5); doc.setFont(B, 'normal'); sc(C.muted);
          doc.text(band.focus, mg + barMaxW + 8, y);
          y += 14;
        }
        y += 8;

        subHeader('Key Insights');
        keyInsights(s2.key_characteristics);

        subHeader('Priority Gaps');
        addBullets(s2.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s2.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 3: Members with Disabilities
      // ═══════════════════════════════════════════════════
      const s3 = sec('disabled');
      if (s3) {
        newPage();
        sectionHeader('Members with Disabilities', '3,046 members (81.4%) | Dominant complexity driver', 'Factor 3');

        opFocus('Disability eligibility characterizes 81.4% of this population, shaping baseline complexity.', '7.7x national rate');

        comparisonBlock('Disability Prevalence', '81.4%', '10.6%', '7.7x', 'Dominant driver');
        metricsBand(s3.metrics_table);

        overlapCallout('Disabled + High-Acuity', '111', '3.0% of population',
          'Highest-intensity members requiring combined disability + utilization care pathways');
        overlapCallout('Disabled + Dual-Eligible', '136', '3.6% of population',
          'Complex coverage coordination with disability-related access needs');

        subHeader('Key Insights');
        keyInsights(s3.key_characteristics, c => !c.label.toLowerCase().includes('no direct'));

        caveatBox('Utilization Data Limitation',
          'This section does not provide ED/IP or readmission rates specifically for disabled members. Overall utilization rates use care-managed denominators and may not generalize to this subpopulation without stratified reporting.');

        subHeader('Priority Gaps');
        addBullets(s3.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s3.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 4: Serious Mental Illness
      // ═══════════════════════════════════════════════════
      const s4 = sec('smi');
      if (s4) {
        newPage();
        sectionHeader('Serious Mental Illness', '83 members (2.2%) | Narrow diagnostic capture requires careful interpretation', 'Factor 4');

        opFocus('All identified SMI is depression (HCC 155). This reflects narrow coding, not clinical absence.', '83 members');

        warningPanel('Definitional Caveat',
          'Depression represents 100% of identified SMI because this report uses HCC 155 only. ' +
          'The national benchmark (17.5%) uses the broader CCW definition (any qualifying diagnosis in claims). ' +
          'The 0.1x ratio reflects this definitional difference, not a true absence of depression. ' +
          'Schizophrenia (HCC 157/158) and Bipolar (HCC 159) show 0 members -- likely a code capture gap, not clinical absence.');

        metricsBand(s4.metrics_table.slice(0, 3));
        metricsTable(s4.metrics_table.slice(3));

        comparisonBlock('Depression Prevalence', '2.2%', '17.5%', '0.1x', 'Definitional difference');

        subHeader('Key Insights');
        keyInsights(s4.key_characteristics);

        subHeader('Priority Gaps');
        addBullets(s4.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s4.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 5: Racial & Ethnic Groups
      // ═══════════════════════════════════════════════════
      const s5 = sec('race_ethnicity');
      if (s5) {
        newPage();
        sectionHeader('Racial & Ethnic Groups', 'Data completeness and disparity interpretation', 'Factor 5');

        opFocus('Race coding must improve before disparity ratios are actionable.', '17.8% Unknown');

        dataQualityAlert('17.8% Unknown/Not Specified Race Coding', '665 members excluded from disparity ratio calculations',
          'Disparity ratios are unreliable until Unknown share drops below current 17% level');

        brk(50);
        doc.setFontSize(8); doc.setFont(B, 'bold'); sc(C.muted); doc.text('POPULATION COMPOSITION', mg, y); y += 12;
        const raceGroups = [
          { name: 'Unknown / Not Specified', count: 665, pct: 17.8, alert: true },
          { name: 'Asian / Pacific Islander', count: 642, pct: 17.2 },
          { name: 'White', count: 623, pct: 16.6 },
          { name: 'Other', count: 611, pct: 16.3 },
          { name: 'Hispanic', count: 601, pct: 16.1 },
          { name: 'Black / African American', count: 600, pct: 16.0 },
        ];
        const raceBarMax = cw * 0.35;
        for (const g of raceGroups) {
          brk(14);
          doc.setFontSize(8); doc.setFont(B, g.alert ? 'bold' : 'normal'); sc(g.alert ? C.amber : C.dark);
          doc.text(g.name, mg, y);
          const barX = mg + 150, barW = (g.pct / 20) * raceBarMax;
          sf(g.alert ? C.amberBg : C.tealLight); doc.rect(barX, y - 6, raceBarMax, 9, 'F');
          sf(g.alert ? C.amber : C.teal); doc.rect(barX, y - 6, barW, 9, 'F');
          doc.setFontSize(7); doc.setFont(B, 'normal'); sc(C.muted);
          doc.text(`${fmtNum(g.count)} (${g.pct}%)`, barX + raceBarMax + 6, y);
          y += 14;
        }
        y += 8;

        caveatBox('Disparity Interpretation',
          'Disparity ratios use care-managed member denominators. Unknown/Not Specified is excluded from calculations. ' +
          'Ratios reflect equity within care-managed scope, not the full attributed population. ' +
          'With 17%+ Unknown, operational equity monitoring is unreliable.');

        subHeader('Priority Gaps');
        addBullets(s5.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s5.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 6: LEP
      // ═══════════════════════════════════════════════════
      const s6 = sec('lep');
      if (s6) {
        newPage();
        sectionHeader('Limited English Proficiency', '139 members (3.7%) | Language access operationalization', 'Factor 6');

        opFocus('Spanish and Chinese cover 74% of LEP members. Prioritize these two workflows.', '139 members');

        comparisonBlock('LEP Prevalence', '3.7%', '8%', '0.5x', 'Below national average');

        subHeader('Language Distribution');
        langRanking(s6.metrics_table.slice(1));

        caveatBox('Data Source',
          'Language data from EHR-reported preferred language. No variant normalization applied. Language-to-resource mapping should use labels as given.');

        subHeader('Priority Gaps');
        addBullets(s6.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s6.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 7a: Dual-Eligible
      // ═══════════════════════════════════════════════════
      const s7a = sec('custom_dual_eligible');
      if (s7a) {
        newPage();
        sectionHeader('Dual-Eligible Members', '294 members (7.9%) | Financial SDOH and high-risk concentration', 'Factor 7a');

        opFocus('43.9% of dual-eligible members are flagged high-risk -- a useful prioritization signal for outreach.', '129 of 294');

        kpiCards([
          { label: 'Total Dual-Eligible', value: '294', note: '7.9% of population' },
          { label: 'Full Dual', value: '192', note: '65.3%' },
          { label: 'Partial Dual', value: '102', note: '34.7%' },
          { label: 'High-Risk Dual', value: '129', note: '43.9% of dual-eligible' },
        ], 4);

        comparisonBlock('Dual-Eligible Rate', '7.9%', '19%', '0.4x', 'Below national average');

        overlapCallout('High-Risk + Dual-Eligible', '129', '43.9% of dual-eligible',
          'Highest-value targeting: nearly half of dual-eligible members have compound risk flags');

        subHeader('Key Insights');
        keyInsights(s7a.key_characteristics);

        subHeader('Priority Gaps');
        addBullets(s7a.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s7a.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // FACTOR 7b: High-Acuity Complex
      // ═══════════════════════════════════════════════════
      const s7b = sec('custom_high_acuity');
      if (s7b) {
        newPage();
        sectionHeader('High-Acuity Complex Members', '206 members (5.5%) | Intensive intervention segment', 'Factor 7b');

        opFocus('This segment is defined by utilization inputs. Elevated metrics confirm segment construction.', '206 members');

        warningPanel('Tautological Metrics Warning',
          'This segment is defined by high utilization and chronic burden. Readmission rate of 100.0%, ' +
          'avg IP visits 4.01, and avg ED visits 5.26 are expected by construction. ' +
          'Do NOT compare these rates to population-level benchmarks. ' +
          'Evaluate this segment on process outcomes (care plan completion, follow-up rates), not utilization benchmarks.');

        kpiCards([
          { label: 'Segment Size', value: '206', note: '5.5% of population' },
          { label: 'Avg IP Visits', value: '4.01', note: 'Per member' },
          { label: 'Avg ED Visits', value: '5.26', note: 'Per member' },
          { label: 'Avg Chronic Conditions', value: '6.4', note: 'vs 2.6 population avg' },
        ], 4);

        brk(30);
        sf(C.redBg); sd(C.redBorder); doc.setLineWidth(0.5); doc.rect(mg, y, cw, 26, 'FD');
        doc.setFontSize(14); doc.setFont(H, 'bold'); sc(C.red);
        doc.text('100.0%', mg + 12, y + 16);
        doc.setFontSize(9); doc.setFont(B, 'bold'); sc(C.dark);
        doc.text('CM Readmission Flag Rate', mg + 70, y + 12);
        doc.setFontSize(7.5); doc.setFont(B, 'normal'); sc(C.muted);
        doc.text('Expected by construction -- this segment is defined by utilization inputs.', mg + 70, y + 22);
        y += 32;

        subHeader('Key Insights');
        keyInsights(s7b.key_characteristics);

        subHeader('Priority Gaps');
        addBullets(s7b.identified_needs, mg + 4, C.body, C.amber);

        subHeader('Recommended Actions');
        structuredActions(s7b.recommended_actions);
      }

      // ═══════════════════════════════════════════════════
      // CROSS-FACTOR SYNTHESIS
      // ═══════════════════════════════════════════════════
      newPage();
      sectionHeader('Cross-Factor Synthesis', 'Compound risk themes and workflow build sequence');

      doc.setFontSize(8); doc.setFont(B, 'bold'); sc(C.muted); doc.text('TOP RISK CONCENTRATIONS', mg, y); y += 10;

      const priorities = this.report.cross_factor_priorities;
      if (priorities) {
        for (let i = 0; i < priorities.length; i++) {
          const item = priorities[i]; brk(50);
          sf(i === 0 ? C.tealBg : C.light); sd(C.border); doc.setLineWidth(0.3);
          const rat = ct(item.rationale); doc.setFontSize(8); doc.setFont(B, 'normal');
          const rLines = doc.splitTextToSize(rat, cw - 40);
          doc.setFontSize(9); doc.setFont(B, 'bold');
          const tLines = doc.splitTextToSize(ct(item.population), cw - 40);
          const cardH = 20 + tLines.length * 11 + 4 + rLines.length * 10 + 16;
          doc.rect(mg, y, cw, cardH, 'FD');
          if (i === 0) { sf(C.teal); doc.rect(mg, y, 3, cardH, 'F'); }

          sf(C.teal); doc.circle(mg + 14, y + 12, 7, 'F');
          doc.setFontSize(8); doc.setFont(B, 'bold'); sc(C.white);
          doc.text(String(item.priority), mg + 14, y + 15, { align: 'center' });
          doc.setFontSize(9); doc.setFont(B, 'bold'); sc(C.dark);
          const titleLines = doc.splitTextToSize(ct(item.population), cw - 40);
          let ty = y + 13; for (const tl of titleLines) { doc.text(tl, mg + 28, ty); ty += 11; }

          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
          let ry = ty + 4; for (const l of rLines) { doc.text(l, mg + 28, ry); ry += 10; }

          if (item.factors_involved) {
            let tagX = mg + 28; doc.setFontSize(6);
            for (const f of item.factors_involved) {
              const tw = doc.getTextWidth(f) + 8;
              sf(C.white); sd(C.border); doc.setLineWidth(0.3); doc.rect(tagX, ry - 4, tw, 10, 'FD');
              doc.setFont(B, 'normal'); sc(C.muted); doc.text(f, tagX + 4, ry + 2); tagX += tw + 3;
            }
          }
          y += cardH + 6;
        }
      }

      y += 8;
      subHeader('Workflow Build Sequence');
      addBullets([
        '1. Complete risk stratification for unstratified members (65.8%) -- prerequisite for all downstream prioritization',
        '2. Establish outreach for high-risk dual-eligible members (129) -- concentrated overlap of financial and clinical risk',
        '3. Develop care pathways for disability + high-acuity overlap (111 members) -- highest-complexity subgroup',
        '4. Improve race/ethnicity coding completeness (665 Unknown) -- required before disparity monitoring is reliable',
        '5. Implement language-concordant outreach for LEP members (Spanish 80, Chinese 23) -- communication access priority',
      ], mg + 4, C.body, C.teal);

      y += 6;
      subHeader('Measurement vs Reality');
      addBullets([
        'Full-population findings: disability prevalence, dual concentration, age structure, unstratified share',
        'Care-managed scope only: utilization rates, readmission flag rate, disparity ratios -- not generalizable to full population',
        'Data completeness gaps: race coding (17.8% Unknown), SMI identification (HCC 155 only), no continuous enrollment filter',
      ], mg + 4, C.body, C.amber);

      // ═══════════════════════════════════════════════════
      // METHODOLOGY APPENDIX
      // ═══════════════════════════════════════════════════
      if (this.report.methodology) {
        const m = this.report.methodology;
        newPage();
        sectionHeader('Methodology & Data Sources', 'Technical appendix');

        const termDef = (term, def) => {
          if (!def) return; brk(30);
          doc.setFontSize(8.5); doc.setFont(B, 'bold'); sc(C.dark); doc.text(ct(term), mg + 4, y); y += 11;
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
          const lines = doc.splitTextToSize(ct(def), cw - 12);
          for (const l of lines) { brk(10); doc.text(l, mg + 4, y); y += 10; }
          y += 7;
        };

        termDef('Assessment Period', m.assessment_period);
        if (m.baseline_note) caveatBox('Baseline Note', m.baseline_note);

        subHeader('Data Sources');
        for (const src of m.data_sources || []) {
          brk(11); doc.setFontSize(8); doc.setFont(B, 'normal');
          sf(C.teal); doc.circle(mg + 5, y - 2.5, 1.5, 'F');
          sc(C.body); doc.text(ct(src), mg + 12, y); y += 11;
        }
        y += 6;

        if (m.data_currency) {
          termDef('Claims Lag', m.data_currency.claims_lag);
          if (m.data_currency.ehr_lag) termDef('EHR Lag', m.data_currency.ehr_lag);
          if (m.data_currency.enrollment_lag) termDef('Enrollment Lag', m.data_currency.enrollment_lag);
        }
        termDef('Attribution Logic', m.attribution_logic);
        termDef('Continuous Enrollment', m.continuous_enrollment);

        if (m.measurement_definitions) {
          subHeader('Measurement Definitions');
          for (const [k, d] of Object.entries(m.measurement_definitions)) {
            const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            termDef(label, d);
          }
        }

        termDef('Overlap Counting', m.overlap_counting);
        termDef('Risk Adjustment Model', m.hcc_model);
        termDef('Small-Cell Suppression', m.suppression_policy);
        termDef('Excluded Populations', m.excluded_populations);

        subHeader('Factor Definitions');
        for (const [, f] of Object.entries(m.factor_definitions || {})) {
          brk(24); sf(C.light);
          const ft = ct(f.method || ''); doc.setFontSize(8);
          const fl = doc.splitTextToSize(ft, cw - 16); const fH = 12 + fl.length * 9.5 + 4;
          doc.rect(mg, y, cw, fH, 'F');
          doc.setFontSize(8.5); doc.setFont(B, 'bold'); sc(C.dark); doc.text(ct(f.name), mg + 6, y + 10);
          doc.setFontSize(8); doc.setFont(B, 'normal'); sc(C.body);
          let fy = y + 20; for (const l of fl) { doc.text(l, mg + 6, fy); fy += 9.5; }
          y += fH + 4;
        }
        y += 4;

        if (m.benchmark_sources) {
          subHeader('Benchmark Sources');
          for (const src of m.benchmark_sources) {
            brk(11); doc.setFontSize(8); doc.setFont(B, 'normal');
            sf(C.teal); doc.circle(mg + 5, y - 2.5, 1.5, 'F');
            sc(C.body); doc.text(ct(src), mg + 12, y); y += 11;
          }
          y += 6;
        }

        if (m.evaluation_methodology) {
          const ev = m.evaluation_methodology;
          subHeader('Evaluation Methodology');
          termDef('Measurement Cadence', ev.measurement_cadence);
          termDef('Intervention Impact', ev.intervention_impact);
          termDef('Year 2 Trending Plan', ev.trending_plan);
        }

        if (m.member_experience) {
          const me = m.member_experience;
          subHeader('Member Experience Data');
          brk(18); sf(C.amberBg);
          const st = `Status: ${ct(me.status)}`; doc.setFontSize(8);
          const sw = doc.getTextWidth(st) + 14; doc.rect(mg, y - 7, sw, 13, 'F');
          doc.setFont(B, 'bold'); sc(C.amber); doc.text(st, mg + 7, y); y += 12;
          termDef('Current State', me.detail);
          termDef('Planned Enhancements', me.planned_enhancements);
        }
      }

      // ── SAVE ──
      pageFooter();
      if (!ticket.confirm()) return;
      doc.save(`Population_Health_Assessment_${this.report.report_date}.pdf`);
      } finally {
        this.exportingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
</style>
